import { BffBusiness } from "./backend-types/business";
import { BffChannel, BffChannelUiGroup } from "./backend-types/channel";
import { BffSucceededChannel } from "./backend-types/common";
import { BffCustomer } from "./backend-types/customer";
import { BffDigitalWallets } from "./backend-types/digital-wallets";
import { BffSession } from "./backend-types/session";
import { internal } from "./internal";
import {
  XenditBusiness,
  XenditCustomer,
  XenditDigitalWallet,
  XenditPaymentChannel,
  XenditPaymentChannelGroup,
  XenditSession,
  XenditSucceededChannel,
} from "./public-data-types";
import {
  assert,
  assertEquals,
  assertNotEquals,
  lockDownInteralProperty,
  removeUndefinedPropertiesFromObject,
  satisfiesMinMax,
} from "./utils";

type XenditItem = NonNullable<XenditSession["items"]>[number];

export function bffSessionToPublic(bffSession: BffSession): XenditSession {
  assertNotEquals(bffSession.session_type, "AUTHORIZATION");
  assertNotEquals(bffSession.mode, "CARDS_SESSION_JS");

  return removeUndefinedPropertiesFromObject<XenditSession>({
    id: bffSession.payment_session_id,
    description: bffSession.description || undefined,
    sessionType: bffSession.session_type,
    mode: bffSession.mode,
    referenceId: bffSession.reference_id,
    country: bffSession.country,
    currency: bffSession.currency,
    amount: bffSession.amount,
    channelProperties: bffSession.channel_properties || undefined,
    expiresAt: new Date(bffSession.expires_at),
    locale: bffSession.locale,
    status: bffSession.status,
    updated: new Date(bffSession.updated),
    successReturnUrl: bffSession.success_return_url,
    cancelReturnUrl: bffSession.cancel_return_url,
    captureMethod: bffSession.capture_method,
    allowSavePaymentMethod: bffSession.allow_save_payment_method,
    subscription: bffSession.subscription
      ? {
          immediatePayment: bffSession.subscription.immediate_payment,
          schedule: {
            anchorDate: new Date(bffSession.subscription.schedule.anchor_date),
            interval: bffSession.subscription.schedule.interval,
            intervalCount: bffSession.subscription.schedule.interval_count,
            retryInterval: bffSession.subscription.schedule.retry_interval,
            retryIntervalCount:
              bffSession.subscription.schedule.retry_interval_count,
            totalRecurrence: bffSession.subscription.schedule.total_recurrence,
            totalRetry: bffSession.subscription.schedule.total_retry,
          },
        }
      : undefined,

    items: bffSession.items?.map((item) => {
      return removeUndefinedPropertiesFromObject<XenditItem>({
        type: item.type,
        referenceId: item.reference_id,
        name: item.name,
        netUnitAmount: item.net_unit_amount,
        quantity: item.quantity,
        url: item.url,
        imageUrl: item.image_url,
        category: item.category,
        subcategory: item.subcategory,
        description: item.description,
        metadata: item.metadata,
      });
    }),
  });
}

export function bffBusinessToPublic(
  bffBusiness: BffBusiness | null,
): XenditBusiness | null {
  if (!bffBusiness) {
    return null;
  }
  return {
    name: bffBusiness.name ?? undefined,
    countryOfOperation: bffBusiness.country_of_operation ?? undefined,
    merchantProfilePictureUrl:
      bffBusiness.merchant_profile_picture_url ?? undefined,
  };
}

export function bffCustomerToPublic(
  bffCustomer: BffCustomer | null,
): XenditCustomer | null {
  if (!bffCustomer) {
    return null;
  }
  assertEquals(bffCustomer.type, "INDIVIDUAL");
  assert(bffCustomer.individual_detail);
  return {
    id: bffCustomer.id,
    type: bffCustomer.type,
    email: bffCustomer.email ?? undefined,
    mobileNumber: bffCustomer.mobile_number ?? undefined,
    individualDetail: {
      givenNames: bffCustomer.individual_detail.given_names ?? undefined,
      surname: bffCustomer.individual_detail.surname ?? undefined,
    },
  };
}

export function bffSucceededChannelToPublic(
  bffSucceededChannel: BffSucceededChannel | null,
): XenditSucceededChannel | null {
  if (!bffSucceededChannel) {
    return null;
  }
  return {
    channelCode: bffSucceededChannel.channel_code,
    logoUrl: bffSucceededChannel.logo_url,
  };
}

type ChannelMarshalConfig = {
  options: {
    filter?: string | string[] | RegExp;
    filterMinMax: boolean;
  };
  pairChannels: PairChannelData;
  session: Pick<BffSession, "amount" | "session_type">;
};

/**
 * Generate public channel groups list.
 */
export function bffUiGroupsToPublic(
  bffChannels: BffChannel[],
  bffChannelGroups: BffChannelUiGroup[],
  marshalConfig: ChannelMarshalConfig,
): XenditPaymentChannelGroup[] {
  const groupsByGroupId = makeGroupsByGroupId(bffChannelGroups);
  const channelsByGroupId = makeChannelsByGroupId(bffChannels, marshalConfig);
  return bffChannelGroups
    .filter((group) => channelsByGroupId[group.id]?.length)
    .map((group) => {
      return bffUiGroupToPublic(
        group,
        channelsByGroupId,
        groupsByGroupId,
        marshalConfig,
      );
    });
}

/**
 * Generate one public channel group.
 */
export function bffUiGroupToPublic(
  bffChannelGroup: BffChannelUiGroup,
  channelsByGroupId: Record<string, BffChannel[]>,
  groupsByGroupId: Record<string, BffChannelUiGroup>,
  marshalConfig: ChannelMarshalConfig,
) {
  const group = removeUndefinedPropertiesFromObject<XenditPaymentChannelGroup>({
    groupId: bffChannelGroup.id,
    label: bffChannelGroup.label,
    get channels() {
      return (channelsByGroupId[bffChannelGroup.id] || []).map((channel) => {
        return bffChannelToPublic(
          channel,
          channelsByGroupId,
          groupsByGroupId,
          marshalConfig,
        );
      });
    },
    [internal]: bffChannelGroup,
  });

  lockDownInteralProperty(group);

  return group;
}

/**
 * Generate one public channel, without group data.
 */
export function singleBffChannelToPublic(
  bffChannel: BffChannel,
  marshalConfig: ChannelMarshalConfig,
): XenditPaymentChannel {
  return bffChannelToPublic(bffChannel, {}, {}, marshalConfig);
}

/**
 * Generate one public channel.
 */
export function bffChannelToPublic(
  bffChannel: BffChannel,
  bffChannelsByGroupId: Record<string, BffChannel[]>,
  bffGroupsByGroupId: Record<string, BffChannelUiGroup>,
  marshalConfig: ChannelMarshalConfig,
): XenditPaymentChannel {
  assert(!marshalConfig.pairChannels.paired[bffChannel.channel_code]);

  const channel = removeUndefinedPropertiesFromObject<XenditPaymentChannel>({
    channelCode:
      marshalConfig.pairChannels.pairs[bffChannel.channel_code]?.map(
        (ch) => ch.channel_code,
      ) ?? bffChannel.channel_code,
    brandName: bffChannel.brand_name,
    brandColor: bffChannel.brand_color,
    brandLogoUrl: bffChannel.brand_logo_url,
    get uiGroup() {
      if (!bffGroupsByGroupId[bffChannel.ui_group]) {
        throw new Error("UI group not found");
      }
      return bffUiGroupToPublic(
        bffGroupsByGroupId[bffChannel.ui_group],
        bffChannelsByGroupId,
        bffGroupsByGroupId,
        marshalConfig,
      );
    },
    minAmount: bffChannel.min_amount,
    maxAmount: bffChannel.max_amount,
    cardBrands: bffChannel.card?.brands.map((b) => {
      return {
        name: b.name,
        logoUrl: b.logo_url,
      };
    }),
    [internal]: marshalConfig.pairChannels.pairs[bffChannel.channel_code] ?? [
      bffChannel,
    ],
  });

  lockDownInteralProperty(channel);

  return channel;
}

/**
 * Generate public channels list.
 */
export function bffChannelsToPublic(
  bffChannels: BffChannel[],
  bffChannelGroups: BffChannelUiGroup[],
  marshalConfig: ChannelMarshalConfig,
): XenditPaymentChannel[] {
  const groupsByGroupId = makeGroupsByGroupId(bffChannelGroups);
  const channelsByGroupId = makeChannelsByGroupId(bffChannels, marshalConfig);
  return bffChannels
    .filter((ch) => {
      return channelFilterFn(ch, marshalConfig);
    })
    .map((channel) => {
      return bffChannelToPublic(
        channel,
        channelsByGroupId,
        groupsByGroupId,
        marshalConfig,
      );
    });
}

/**
 * Make a mapping of group ID to group.
 */
function makeGroupsByGroupId(
  bffChannelGroups: BffChannelUiGroup[],
): Record<string, BffChannelUiGroup> {
  const groupMap: Record<string, BffChannelUiGroup> = {};
  for (const bffGroup of bffChannelGroups) {
    groupMap[bffGroup.id] = bffGroup;
  }
  return groupMap;
}

/**
 * Returns the visible channel list for a telemetry event.
 */
export function getChannelCodesForTelemetry(
  session: BffSession,
  channels: BffChannel[],
  uiGroup: string | null,
) {
  const marshalConfig: ChannelMarshalConfig = {
    pairChannels: {
      pairs: {},
      paired: {},
    },
    options: {
      filterMinMax: true,
    },
    session,
  };

  return channels
    .filter((channel) => channelFilterFn(channel, marshalConfig))
    .filter((channel) => {
      if (uiGroup === null) return true;
      return channel.ui_group === uiGroup;
    })
    .map((channel) => channel.channel_code);
}

/**
 * Return true if the channel passes the filter criteria.
 */
export function channelFilterFn(
  channel: BffChannel,
  marshalConfig: ChannelMarshalConfig,
) {
  if (marshalConfig.pairChannels.paired[channel.channel_code]) {
    // channel is a second member of a pair, skip
    // TODO: users may filter out the first member of a pair, in which case we should remap the second member to the first
    return false;
  }

  if (
    marshalConfig.options.filterMinMax &&
    !satisfiesMinMax(marshalConfig.session, channel)
  ) {
    // min/max amount not satisfied
    return false;
  }

  const filter = marshalConfig.options.filter;
  if (filter && !channelCodeMatchesFilter(filter, channel.channel_code)) {
    // channel code does not match filter
    return false;
  }

  return true;
}

/**
 * Checks a channel code string against a filter term.
 */
function channelCodeMatchesFilter(
  filter: string | string[] | RegExp,
  code: string,
) {
  if (typeof filter === "string" && code === filter) {
    return true;
  }
  if (Array.isArray(filter) && filter.includes(code)) {
    return true;
  }
  if (filter instanceof RegExp && filter.test(code)) {
    return true;
  }
  return false;
}

/**
 * Create a mapping of group ID to channels in that group, applying channel filtering and removing empty groups.
 */
export function makeChannelsByGroupId(
  bffChannels: BffChannel[],
  marshalConfig: ChannelMarshalConfig,
): Record<string, BffChannel[]> {
  const channelsByGroupId: Record<string, BffChannel[]> = {};
  for (const bffChannel of bffChannels) {
    if (!channelFilterFn(bffChannel, marshalConfig)) {
      continue;
    }
    const groupId = bffChannel.ui_group;
    if (!channelsByGroupId[groupId]) {
      channelsByGroupId[groupId] = [];
    }
    channelsByGroupId[groupId].push(bffChannel);
  }
  return channelsByGroupId;
}

type PairChannelData = {
  /** Map of channel_code of first member of pair to an array containing the pair */
  pairs: Record<string, [BffChannel, BffChannel]>;
  /** Set of channel_codes of second members of pairs */
  paired: Record<string, boolean>;
};

/**
 * Finds channels that are pairs (i.e., differ only by allow_save) and groups them together.
 */
export function findChannelPairs(bffChannels: BffChannel[]): PairChannelData {
  const brandMap = new Map<string, BffChannel[]>();
  for (const channel of bffChannels) {
    const brandName = channel.brand_name;
    if (!brandMap.has(brandName)) {
      brandMap.set(brandName, []);
    }
    brandMap.get(brandName)!.push(channel);
  }

  const pairs: Record<string, [BffChannel, BffChannel]> = {};
  const paired: Record<string, boolean> = {};
  for (const [_, channels] of brandMap) {
    for (const ch of channels) {
      const isFirst = channels[0].allow_save === false;
      if (isFirst) {
        const pair = channels.find(
          (other) =>
            ch.channel_code !== other.channel_code &&
            ch.ui_group === other.ui_group &&
            other.allow_save === true,
        );
        if (pair) {
          pairs[ch.channel_code] = [ch, pair];
          paired[pair.channel_code] = true;
        }
      }
    }
  }

  return {
    pairs,
    paired,
  };
}

export function bffDigitalWalletsToPublic(
  bffDigitalWallets: BffDigitalWallets,
  bffChannels: BffChannel[],
  bffChannelGroups: BffChannelUiGroup[],
  marshalConfig: ChannelMarshalConfig,
): XenditDigitalWallet[] {
  const out: XenditDigitalWallet[] = [];

  const groupsByGroupId = makeGroupsByGroupId(bffChannelGroups);

  if (bffDigitalWallets.google_pay) {
    const googlePayChannels =
      bffDigitalWallets.google_pay.allowed_payment_methods.map((method) => {
        const ch = bffChannels.find(
          (c) => c.channel_code === method.channel_code,
        );
        assert(ch);
        return ch;
      });

    const channelsByGroupId = makeChannelsByGroupId(
      googlePayChannels,
      marshalConfig,
    );

    out.push({
      digitalWalletCode: "GOOGLE_PAY",
      get channels() {
        return googlePayChannels.map((channel) => {
          return bffChannelToPublic(
            channel,
            channelsByGroupId,
            groupsByGroupId,
            marshalConfig,
          );
        });
      },
      [internal]: true,
    });
  }

  if (bffDigitalWallets.apple_pay) {
    // Apple Pay not send channel list like googlepay, and only supports CARDS
    const cardsChannel = bffChannels.find((c) => c.channel_code === "CARDS");

    if (cardsChannel) {
      const channelsByGroupId = makeChannelsByGroupId(
        [cardsChannel],
        marshalConfig,
      );

      out.push({
        digitalWalletCode: "APPLE_PAY",
        get channels() {
          return [
            bffChannelToPublic(
              cardsChannel,
              channelsByGroupId,
              groupsByGroupId,
              marshalConfig,
            ),
          ];
        },
        [internal]: true,
      });
    }
  }

  return out;
}
